/*
 * Copyright (c) 2020. American Express Company
 */

// third-party
import * as _ from 'lodash';
import $ from 'jquery';
import React, { useState } from 'react';
import { DateTimeFormatter, LocalDate, Month } from '@js-joda/core';

// models and services
import { AccountModel } from 'src/types/account.model';
import { FilterDate } from 'src/types/filter-date.enum';
import { CashFlowModel } from 'src/types/cash-flow.model';
import { CategoryModel } from 'src/types/category.model';
import { MenuItemModel, SASVariant } from 'src/types/view-models/search-and-select.view.model';
import { TransactionModel } from 'src/types/transaction.model';
import FormatService from '../../../../services/format.service';
import LiteralsService from 'src/services/literals/LiteralsService';
import NavigationService from '../../../../services/navigation.service';
import SearchService from '../../../../services/search.service';
import { TransactionType } from 'src/types/transaction-type.enum';
import { TransactionsViewViewModel } from 'src/types/view-models/transactions/transactionsView.view.model';
import { DLSSize } from 'src/types/dls-size.enum';

// components
import BackButton from 'src/components/back-button/BackButton';
import ClickableIcon from 'src/bw/clickable-icon/ClickableIcon';
import ClickAwayListener from '@mui/base/ClickAwayListener';
import SearchAndSelect from '../../../search-and-select/SearchAndSelect';
import { ButtonTertiary, DateRangePicker, IconCancelCircle, Search, ToggleSwitch } from '@americanexpress/dls-react';
import Pagination from '@mui/lab/Pagination';
import Popover from '@mui/material/Popover/Popover';
import RoundedDropdown from 'src/components/menus/RoundedDropdown';
import Typography from '../../../typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import WideView from 'src/components/atomic/layouts/wideView/WideView';
import UnifiedTransactionsTable, {
    TransactionsTableContext
} from 'src/components/atomic/organisms/unified-transactions/UnifiedTransactionsTable';

// icons and styles
import colors from 'src/styles/colors.module.scss';
import styles from './transactionsView.module.scss';
import Selectors from 'src/utils/selectors';

/** Returns the date limits for the custom date popover based on the beginning and start of the user's cashflow */
function getDateLimits(cashFlow: CashFlowModel[]): [string, string] {
    let earliestStartDate: LocalDate = LocalDate.now();
    let latestEndDate: LocalDate = LocalDate.now();

    for (const e of cashFlow) {
        if (e.startDate < earliestStartDate) earliestStartDate = e.startDate;
        if (latestEndDate < e.endDate) latestEndDate = e.endDate;
    }

    return [FormatService.formatDate(earliestStartDate), FormatService.formatDate(latestEndDate)];
}

export default function TransactionsView({
    groupUid,
    history,
    categories,
    startDate,
    endDate,
    filteredTransactions,
    type,
    incomeSource,
    incomeSources,
    account,
    accounts,
    showHidden,
    selectedTransactions,
    displayedTransactions,
    pageSize,
    page,
    includedAccounts,
    drawerProps,
    user,
    handleBack,
    setTransactions,
    updateFilteredTransactions,
    updateDrawer,
    handleReloadUser
}: TransactionsViewViewModel): JSX.Element {
    const [anchor, setAnchor] = useState<any>();
    const [search, setSearch] = useState<string>();
    const [customStartDate, setCustomStartDate] = useState<LocalDate>();
    const [customEndDate, setCustomEndDate] = useState<LocalDate>();

    const incomeTransactions: TransactionModel[] = _.filter(filteredTransactions, (transaction) => {
        return transaction.type === TransactionType.TRANSACTION_INCOME && !transaction.ignore;
    });
    const expenseTransactions: TransactionModel[] = _.filter(filteredTransactions, (transaction) => {
        return transaction.type === TransactionType.TRANSACTION_EXPENSE && !transaction.ignore;
    });
    const income: number = _.sumBy(incomeTransactions, 'amount') * -1;
    const expense: number = _.sumBy(expenseTransactions, 'amount');
    const cashFlow: number = income - expense;
    const cashFlowTitleStyle: string = cashFlow > 0 ? colors.good : cashFlow < 0 ? colors.bad : null;
    const [earliestCashFlowStartDate, latestCashFlowEndDate]: [string, string] = getDateLimits(user.cashFlow);
    const menuItemModels: MenuItemModel[] = SearchAndSelect.getMenuItemModels(categories);
    const headerCategory: CategoryModel = groupUid ? _.find(categories, { id: groupUid }) : null;

    /** Sets anchor to open the custom date popover */
    const onCustomClick = (event: any): void => {
        setAnchor(event.currentTarget);
    };

    /** Sets anchor to close the custom date popover */
    const onCustomClose = (): void => {
        setAnchor(null);
    };

    /** Update the transactions view with the custom start date and end date provided by the user */
    const setCustomDates = async (startDate: LocalDate, endDate: LocalDate): Promise<void> => {
        const newDrawerProps: any = await setTransactions(drawerProps, startDate, endDate);
        updateDrawer(newDrawerProps);
        await NavigationService.goTo(
            history,
            SearchService.getUriWithParam(window.location.href, {
                start: FormatService.formatYearMonthDate(startDate),
                end: FormatService.formatYearMonthDate(endDate),
                page: 1
            })
        );
    };

    /** Update the transactions view when the user chooses a specific filter */
    const handleUpdate = (field: string, value: any): void => {
        let newDrawerProps = { ...drawerProps };
        newDrawerProps[field] = value;

        switch (field) {
            case 'account':
                NavigationService.goTo(
                    history,
                    SearchService.getUriWithParam(window.location.href, {
                        account: value,
                        page: 1
                    })
                ).catch(console.error);
                newDrawerProps.page = 1;
                break;
            case 'groupUid':
                NavigationService.goTo(
                    history,
                    SearchService.getUriWithParam(window.location.href, {
                        group: value,
                        page: 1
                    })
                ).catch(console.error);
                newDrawerProps.page = 1;
                break;
            case 'incomeSource':
                NavigationService.goTo(
                    history,
                    SearchService.getUriWithParam(window.location.href, {
                        incomesource: value,
                        page: 1
                    })
                ).catch(console.error);
                newDrawerProps.page = 1;
                break;
            case 'page':
                NavigationService.goTo(
                    history,
                    SearchService.getUriWithParam(window.location.href, { page: value })
                ).catch(console.error);
                break;
            case 'search':
                NavigationService.goTo(
                    history,
                    SearchService.getUriWithParam(window.location.href, {
                        search: value,
                        page: 1
                    })
                ).catch(console.error);
                newDrawerProps.page = 1;
                break;
            case 'type':
                NavigationService.goTo(
                    history,
                    SearchService.getUriWithParam(window.location.href, {
                        type: value,
                        page: 1
                    })
                ).catch(console.error);
                newDrawerProps.page = 1;
                break;
        }

        newDrawerProps = updateFilteredTransactions(newDrawerProps);
        updateDrawer(newDrawerProps);
    };

    /** From the start and end date, figure out what date the user selected from the menu */
    const getCurrentDate = (): string => {
        if (!startDate) return '';

        const now: LocalDate = LocalDate.now();
        const startDateStr: string = FormatService.formatYearMonthDate(startDate);
        const endDateStr: string = FormatService.formatYearMonthDate(endDate);

        switch (endDateStr) {
            case FormatService.formatYearMonthDate(now):
                // these formats all end with endDate === today
                switch (startDateStr) {
                    case FormatService.formatYearMonthDate(now.minusDays(1)):
                        return FilterDate.YESTERDAY;
                    case FormatService.formatYearMonthDate(now.minusDays(7)):
                        return FilterDate.LAST_SEVEN_DAYS;
                    default:
                        return FilterDate.CUSTOM;
                }
            case FormatService.formatYearMonthDate(now.withDayOfMonth(1)):
                // these formats all end with endDate === 1st of this month
                switch (startDateStr) {
                    case FormatService.formatYearMonthDate(now.withDayOfMonth(1).minusMonths(1)):
                        return FilterDate.LAST_MONTH;
                    case FormatService.formatYearMonthDate(now.withDayOfMonth(1).minusMonths(12)):
                        return FilterDate.LAST_TWELVE_MONTHS;
                    default:
                        return FilterDate.CUSTOM;
                }
            default:
                if (
                    startDateStr ===
                    FormatService.formatYearMonthDate(LocalDate.of(now.year(), Month.JANUARY, 1).minusYears(1))
                ) {
                    if (endDateStr === FormatService.formatYearMonthDate(LocalDate.of(now.year(), Month.JANUARY, 1))) {
                        return FilterDate.LAST_YEAR;
                    }
                }

                return FilterDate.CUSTOM;
        }
    };

    /** Update the transactions view when the user chooses a specific date filter from the dropdown */
    const goToNewDate = async (dateStr: string): Promise<void> => {
        let startDate: LocalDate;
        const now: LocalDate = LocalDate.now();
        const end: string = FormatService.formatYearMonthDate(now);
        let newDrawerProps: any;

        switch (dateStr) {
            case FilterDate.NONE:
                newDrawerProps = await setTransactions(drawerProps);
                updateDrawer(newDrawerProps);
                await NavigationService.goTo(
                    history,
                    SearchService.getUriWithParam(window.location.href, { start: null, end: null, page: 1 })
                );
                break;
            case FilterDate.YESTERDAY:
                startDate = now.minusDays(1);
                newDrawerProps = await setTransactions(drawerProps, startDate, now);
                updateDrawer(newDrawerProps);
                await NavigationService.goTo(
                    history,
                    SearchService.getUriWithParam(window.location.href, {
                        start: FormatService.formatYearMonthDate(startDate),
                        end,
                        page: 1
                    })
                );
                break;
            case FilterDate.LAST_SEVEN_DAYS:
                startDate = now.minusDays(7);
                newDrawerProps = await setTransactions(drawerProps, startDate, now);
                updateDrawer(newDrawerProps);
                await NavigationService.goTo(
                    history,
                    SearchService.getUriWithParam(window.location.href, {
                        start: FormatService.formatYearMonthDate(startDate),
                        end,
                        page: 1
                    })
                );
                break;
            case FilterDate.LAST_MONTH:
                startDate = now.withDayOfMonth(1).minusMonths(1);
                const endOfMonth: LocalDate = startDate.plusMonths(1);
                newDrawerProps = await setTransactions(drawerProps, startDate, endOfMonth);
                updateDrawer(newDrawerProps);
                await NavigationService.goTo(
                    history,
                    SearchService.getUriWithParam(window.location.href, {
                        start: FormatService.formatYearMonthDate(startDate),
                        end: FormatService.formatYearMonthDate(endOfMonth),
                        page: 1
                    })
                );
                break;
            case FilterDate.LAST_TWELVE_MONTHS:
                startDate = now.withDayOfMonth(1).minusMonths(12);
                const endDate: LocalDate = now.withDayOfMonth(1);
                newDrawerProps = await setTransactions(drawerProps, startDate, now);
                updateDrawer(newDrawerProps);
                await NavigationService.goTo(
                    history,
                    SearchService.getUriWithParam(window.location.href, {
                        start: FormatService.formatYearMonthDate(startDate),
                        end: FormatService.formatYearMonthDate(endDate),
                        page: 1
                    })
                );
                break;
            case FilterDate.LAST_YEAR:
                startDate = LocalDate.of(now.year(), Month.JANUARY, 1).minusYears(1);
                const endOfYear: LocalDate = startDate.plusYears(1);
                newDrawerProps = await setTransactions(drawerProps, startDate, endOfYear);
                updateDrawer(newDrawerProps);
                await NavigationService.goTo(
                    history,
                    SearchService.getUriWithParam(window.location.href, {
                        start: FormatService.formatYearMonthDate(startDate),
                        end: FormatService.formatYearMonthDate(endOfYear),
                        page: 1
                    })
                );
                break;
        }
    };

    /** The custom date popover that appears when the user wants to filter transactions for a specific date */
    const customDatePopover: JSX.Element = (
        <Popover
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'left'
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
            }}
            open={Boolean(anchor)}
            PaperProps={{ className: 'dr-container' }}
            anchorEl={anchor}
        >
            <ClickAwayListener onClickAway={onCustomClose}>
                <div className="dr-inner">
                    <Typography variant={TypographyVariants.HEADING_S} className="picker-title">
                        {LiteralsService.getLiteral('TransactionsView.dateRange')}
                    </Typography>
                    <DateRangePicker
                        dates={{
                            disabled: [false],
                            min: earliestCashFlowStartDate,
                            max: latestCashFlowEndDate,
                            start: customStartDate
                                ? customStartDate.format(DateTimeFormatter.ofPattern('M/d/yyyy'))
                                : LocalDate.now().minusDays(3).format(DateTimeFormatter.ofPattern('M/d/yyyy')),
                            end: customEndDate
                                ? customEndDate.format(DateTimeFormatter.ofPattern('M/d/yyyy'))
                                : LocalDate.now().plusDays(3).format(DateTimeFormatter.ofPattern('M/d/yyyy'))
                        }}
                        ids={{
                            end: 'drpicker-end',
                            start: 'drpicker-start'
                        }}
                        labels={{
                            end: 'End Date',
                            endCalendarButton: 'End Date Toggle Calendar',
                            nextMonth: 'Next month',
                            previousMonth: 'Previous month',
                            start: 'Start Date',
                            startCalendarButton: 'Start Date Toggle Calendar'
                        }}
                        onChange={(
                            { target: { name } }: { target: { name: string } },
                            { dateObject }: { dateObject: Date }
                        ) => {
                            if (name && dateObject) {
                                const newLocalDate = LocalDate.parse(dateObject.toISOString().split('T')[0]);

                                if (name === 'drpicker-start') setCustomStartDate(newLocalDate);
                                else if (name === 'drpicker-end') setCustomEndDate(newLocalDate);
                            }
                        }}
                        status={{
                            end: 'default',
                            start: 'default'
                        }}
                    />
                    <div className="dr-button-bar">
                        <ButtonTertiary
                            size="sm"
                            onClick={() => {
                                if (customStartDate && customEndDate)
                                    setCustomDates(customStartDate, customEndDate).catch(console.error);
                                onCustomClose();
                            }}
                        >
                            {LiteralsService.getLiteral('CommonText.update')}
                        </ButtonTertiary>
                        <ButtonTertiary size="sm" borderless={true} onClick={onCustomClose}>
                            {LiteralsService.getLiteral('CommonText.cancel')}
                        </ButtonTertiary>
                    </div>
                </div>
            </ClickAwayListener>
        </Popover>
    );

    return (
        <WideView
            hasBackgroundBanner
            showFooter
            hideBackButton
            classNameView={styles.transactionsView}
            data-cy={Selectors.SpendSmart.BUDGET_VIEW}
            titleElement={
                <div className={styles.titleWrapper}>
                    <BackButton
                        handleBack={async () => {
                            if (drawerProps.transactionsChanged) {
                                await handleReloadUser();
                            }
                            handleBack();
                        }}
                        onDarkBackground
                        size={DLSSize.MEDIUM}
                    />
                    <Typography variant={TypographyVariants.HEADING_XL} className={styles.title}>
                        {LiteralsService.getLiteral('TransactionsView.title')}
                    </Typography>
                </div>
            }
            subtitleElement={
                <Typography variant={TypographyVariants.HEADING_S} className={styles.subtitle}>
                    {LiteralsService.getLiteral('TransactionsView.subtitle')}
                </Typography>
            }
            headerChildren={<>{customDatePopover}</>}
        >
            <div className={styles.contentArea}>
                <div className={styles.topRow}>
                    <div className={styles.transactionOverview}>
                        <Typography variant={TypographyVariants.HEADING_L} className={colors.brandStrong}>
                            {LiteralsService.getLiteral('TransactionsView.noOfTransactions', [
                                filteredTransactions?.length || 0
                            ])}
                        </Typography>
                        <div>
                            {income !== 0 && (
                                <div className={styles.incomeExpenseInfo}>
                                    <Typography
                                        variant={TypographyVariants.HEADING_XS}
                                        data-cy={Selectors.Transactions.TOTAL_INCOME}
                                    >
                                        {FormatService.format(income)}
                                    </Typography>
                                    <Typography component="p" variant={TypographyVariants.SUBTEXT}>
                                        {LiteralsService.getLiteral('TransactionsView.incomeOverview')}
                                    </Typography>
                                </div>
                            )}
                            {expense !== 0 && (
                                <div className={styles.incomeExpenseInfo}>
                                    <Typography
                                        variant={TypographyVariants.HEADING_XS}
                                        data-cy={Selectors.Transactions.TOTAL_EXPENSES}
                                    >
                                        {FormatService.format(expense)}
                                    </Typography>
                                    <Typography component="p" variant={TypographyVariants.SUBTEXT}>
                                        {LiteralsService.getLiteral('TransactionsView.expensesOverview')}
                                    </Typography>
                                </div>
                            )}
                            {income !== 0 && expense !== 0 && (
                                <div className={styles.cashFlowInfo}>
                                    <Typography
                                        variant={TypographyVariants.HEADING_XS}
                                        className={cashFlowTitleStyle}
                                        data-cy={Selectors.Transactions.TOTAL_CASH_FLOW}
                                    >
                                        {FormatService.formatCurrency(cashFlow, { round: true, sign: true })}
                                    </Typography>
                                    <Typography component="p" variant={TypographyVariants.SUBTEXT}>
                                        {LiteralsService.getLiteral('TransactionsView.cashFlowOverview')}
                                    </Typography>
                                </div>
                            )}
                        </div>
                    </div>
                    <Search
                        placeholder={LiteralsService.getLiteral('TransactionsView.searchPlaceholder')}
                        value={search || ''}
                        buttonSearch
                        data-cy={Selectors.Transactions.SEARCH}
                        onChange={(e: any) => {
                            // DLS recommends using event type to identify when the user has clicked the
                            // clear (x) button to clear the search field
                            if (e.type === 'click') {
                                handleUpdate('search', '');
                                setSearch('');
                            } else {
                                setSearch(e.target.value);
                            }
                        }}
                        onSearch={() => {
                            handleUpdate('search', search);
                        }}
                    />
                </div>
                <div className={styles.filtersRow}>
                    <div className={styles.left}>
                        <div className={styles.filters}>
                            <div className={`${styles.filterMenu} ${styles.bigphone}`}>
                                <RoundedDropdown
                                    onChange={(e) => {
                                        if (e.target.value === FilterDate.CUSTOM) {
                                            onCustomClick(e);
                                            return;
                                        }
                                        goToNewDate(e.target.value).catch(console.error);
                                    }}
                                    value={getCurrentDate() || FilterDate.NONE}
                                >
                                    <option value={FilterDate.NONE}>
                                        {LiteralsService.getLiteral('TransactionsView.filters.date.title')}
                                    </option>
                                    <option value={FilterDate.YESTERDAY}>
                                        {LiteralsService.getLiteral('TransactionsView.filters.date.yesterday')}
                                    </option>
                                    <option value={FilterDate.LAST_SEVEN_DAYS}>
                                        {LiteralsService.getLiteral('TransactionsView.filters.date.lastWeek')}
                                    </option>
                                    <option value={FilterDate.LAST_MONTH}>
                                        {LiteralsService.getLiteral('TransactionsView.filters.date.lastMonth')}
                                    </option>
                                    <option value={FilterDate.LAST_TWELVE_MONTHS}>
                                        {LiteralsService.getLiteral('TransactionsView.filters.date.lastTwelveMonths')}
                                    </option>
                                    <option value={FilterDate.LAST_YEAR}>
                                        {LiteralsService.getLiteral('TransactionsView.filters.date.lastYear')}
                                    </option>
                                    <option value={FilterDate.CUSTOM}>
                                        {startDate && getCurrentDate() === FilterDate.CUSTOM
                                            ? `${FormatService.formatDate(startDate)}-${FormatService.formatDate(
                                                  endDate
                                              )}`
                                            : 'Custom'}
                                    </option>
                                </RoundedDropdown>
                                {startDate ? (
                                    <div data-cy={Selectors.Transactions.CLOSE}>
                                        <ClickableIcon
                                            icon={<IconCancelCircle />}
                                            className={styles.close}
                                            onClick={async () => {
                                                const newDrawerProps: any = await setTransactions(drawerProps);
                                                updateDrawer(newDrawerProps);
                                                await NavigationService.goTo(
                                                    history,
                                                    SearchService.getUriWithParam(window.location.href, {
                                                        start: null,
                                                        end: null,
                                                        page: 1
                                                    })
                                                );
                                            }}
                                        />
                                    </div>
                                ) : null}
                            </div>
                            <div
                                className={`${styles.filterMenu} ${
                                    type === TransactionType.TRANSACTION_INCOME ? 'disabled' : ''
                                }`}
                            >
                                <SearchAndSelect
                                    name={headerCategory ? headerCategory.name : 'Category'}
                                    title={LiteralsService.getLiteral('TransactionsView.filters.category.title')}
                                    className="group-select"
                                    items={menuItemModels}
                                    variant={SASVariant.ROUNDED}
                                    expanded={headerCategory ? [headerCategory.parentId] : []}
                                    selected={groupUid}
                                    onClick={(item: MenuItemModel) => {
                                        handleUpdate('groupUid', item.value);
                                    }}
                                />
                                {groupUid?.length ? (
                                    <div data-cy={Selectors.Transactions.CLOSE}>
                                        <ClickableIcon
                                            icon={<IconCancelCircle />}
                                            className={`${styles.close} ${styles.groupClose}`}
                                            onClick={() => {
                                                $('.category-menu-rounded').find('div').first().text('Select Category');
                                                handleUpdate('groupUid', null);
                                            }}
                                        />
                                    </div>
                                ) : null}
                            </div>
                            <div
                                className={`${styles.tablet} ${styles.filterMenu} ${
                                    type === TransactionType.TRANSACTION_EXPENSE ? 'disabled' : ''
                                }`}
                            >
                                <RoundedDropdown
                                    value={incomeSource || 'none'}
                                    onChange={(e) => {
                                        if (Number(e.target.value) !== -1) {
                                            handleUpdate('incomeSource', e.target.value);
                                        }
                                    }}
                                    disabled={type === TransactionType.TRANSACTION_EXPENSE}
                                    className={styles.incomeSource}
                                >
                                    <option key={'source'} value="none">
                                        {LiteralsService.getLiteral('TransactionsView.filters.income.title')}
                                    </option>
                                    {incomeSources.map((incomeSource: any) => {
                                        return (
                                            <option
                                                key={`incomeSource${incomeSource.incomeUid}`}
                                                value={incomeSource.incomeUid}
                                            >
                                                {incomeSource.name}
                                            </option>
                                        );
                                    })}
                                    <option value={-1}>
                                        {LiteralsService.getLiteral('TransactionsView.filters.income.newIncomeSource')}
                                    </option>
                                </RoundedDropdown>
                                {incomeSource?.length ? (
                                    <div data-cy={Selectors.Transactions.CLOSE}>
                                        <ClickableIcon
                                            icon={<IconCancelCircle />}
                                            className={styles.close}
                                            onClick={() => {
                                                handleUpdate('incomeSource', null);
                                            }}
                                        />
                                    </div>
                                ) : null}
                            </div>
                            {accounts ? (
                                <div className={styles.filterMenu}>
                                    <RoundedDropdown
                                        onChange={(e) => {
                                            handleUpdate('account', e.target.value);
                                        }}
                                        value={account || -1}
                                    >
                                        <option key={'account-null'} value={-1}>
                                            {LiteralsService.getLiteral('TransactionsView.filters.account.title')}
                                        </option>
                                        {accounts.map((account: AccountModel) => {
                                            if (!includedAccounts.includes(account.id)) return null;

                                            return (
                                                <option key={`account${account.id}`} value={account.id}>
                                                    {account.name}
                                                </option>
                                            );
                                        })}
                                    </RoundedDropdown>
                                    {account ? (
                                        account.toString() !== '-1' ? (
                                            <div data-cy={Selectors.Transactions.CLOSE}>
                                                <ClickableIcon
                                                    icon={<IconCancelCircle />}
                                                    className={styles.close}
                                                    onClick={() => {
                                                        handleUpdate('account', null);
                                                    }}
                                                />
                                            </div>
                                        ) : null
                                    ) : null}
                                </div>
                            ) : null}
                            <div
                                className={`${styles.filterMenu} ${styles.bigphone}`}
                                data-cy={Selectors.Transactions.TYPE_FILTER}
                            >
                                <RoundedDropdown
                                    onChange={(e) => {
                                        handleUpdate('type', e.target.value);
                                    }}
                                    value={type || ''}
                                >
                                    <option key={'type'} value="">
                                        {LiteralsService.getLiteral('TransactionsView.filters.type.title')}
                                    </option>
                                    <option key={'income'} value={TransactionType.TRANSACTION_INCOME}>
                                        {LiteralsService.getLiteral('TransactionsView.filters.type.income')}
                                    </option>
                                    <option key={'expense'} value={TransactionType.TRANSACTION_EXPENSE}>
                                        {LiteralsService.getLiteral('TransactionsView.filters.type.expense')}
                                    </option>
                                </RoundedDropdown>
                                {type?.length ? (
                                    <div data-cy={Selectors.Transactions.CLOSE}>
                                        <ClickableIcon
                                            icon={<IconCancelCircle />}
                                            className={styles.close}
                                            onClick={() => {
                                                handleUpdate('type', null);
                                            }}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div className={styles.right}>
                        <div>
                            <Typography variant={TypographyVariants.HEADING_XS} className={colors.neutral}>
                                {LiteralsService.getLiteral('TransactionsView.hiddenLabel')}
                            </Typography>
                            <ToggleSwitch
                                checked={showHidden || undefined}
                                id="toggleSwitch"
                                onClick={() => handleUpdate('showHidden', !showHidden)}
                            />
                        </div>
                        <ButtonTertiary
                            className="right desktop"
                            onClick={async () =>
                                await NavigationService.goTo(history, '/drawer/rules', 'Expense rules drawer')
                            }
                            key="investment"
                            id="add-asset"
                        >
                            {LiteralsService.getLiteral('TransactionsView.manageRules')}
                        </ButtonTertiary>
                    </div>
                </div>
                <TransactionsTableContext.Consumer>
                    {(ctx) => (
                        <UnifiedTransactionsTable
                            key="transactions-table"
                            drawerProps={drawerProps}
                            showHidden={showHidden}
                            saveLabel={'Save'}
                            handleBack={handleBack}
                            handleReloadUser={ctx.handleReloadUser}
                            handleChangeIncomeSource={ctx.handleChangeIncomeSource}
                            handleDisplayTransaction={ctx.handleDisplayTransaction}
                            handleIgnoreFutureTransaction={ctx.handleIgnoreFutureTransaction}
                            handleSortUnifiedTransaction={ctx.handleSortUnifiedTransaction}
                            handleChangeTransactionGroup={ctx.handleChangeTransactionGroup}
                            recategorizeSelectedIncomes={ctx.recategorizeSelectedIncomes}
                            recategorizeSelectedExpenses={ctx.recategorizeSelectedExpenses}
                            changeFrequenceOfSelected={ctx.changeFrequenceOfSelected}
                            selectedTransactions={selectedTransactions || []}
                            transactions={displayedTransactions}
                            isReview={false}
                            updateDrawer={updateDrawer}
                            user={user}
                            handleDeleteIncomeRule={ctx.handleDeleteIncomeRule}
                            handleDeleteRule={ctx.handleDeleteRule}
                            updateFilteredTransactions={updateFilteredTransactions}
                            review={false}
                            getLatestTransactions={false}
                            requireHeader={true}
                        />
                    )}
                </TransactionsTableContext.Consumer>
                {filteredTransactions.length > pageSize ? (
                    <Pagination
                        count={Math.ceil(filteredTransactions.length / pageSize)}
                        page={page}
                        onChange={(e: any, page: number) => {
                            handleUpdate('page', page);
                        }}
                        variant="outlined"
                        shape="rounded"
                        className="pagination"
                    />
                ) : null}
            </div>
        </WideView>
    );
}
